import cloneDeep from 'lodash/cloneDeep'
import { formatFloorKey, toOrdinal } from 'plan-library-common'

import { COLUMN_LABELS } from '../components/Plans/utils/constants'
import { planCategories } from '../lib/constants'
import { planElevationObj } from '../redux/slice/createPlanSlice'
import {
  ICommunityNumber,
  IPlanElevation,
  PlanArea,
  PlanMasterData,
  PlanOverview,
  PlanType,
} from '../types/plan.interface'
import { ShellOverview } from '../types/shell.interface'
import { UnitOverview } from '../types/unit.interface'
import { splitMultiValued } from './conversion.util'

const mapBooleanToYN = (
  value: boolean | undefined,
  useDefaults: boolean = true
): 'Y' | 'N' | undefined => {
  if (useDefaults) {
    return value ? 'Y' : 'N'
  }
  return value === true ? 'Y' : value === false ? 'N' : undefined
}

const returnUndefinedIfUseDefaults = (
  value: boolean | undefined,
  useDefaults: boolean = true
): boolean | undefined => {
  if (useDefaults) {
    return value ?? false
  }
  return value
}

const toPlanToView = (planData: PlanType, useDefaults: boolean = true) => {
  const {
    additionalFacilities: {
      deck,
      deckSize,
      impactWindows,
      indoorFireplace,
      interiorSprinklers,
      lanaiEnclosures,
      lanaiEnclosureType,
      lanaiSqft,
      numberOfIndoorFireplaces,
      numberOfOutdoorFireplaces,
      outdoorFireplace,
      outdoorKitchen,
      patio,
      patioCovering,
      patioSize,
      stormShutters,
      vaultedCeiling,
      vaultedCeilingLivingSpaceImpacts,
      vaultedCeilingLocation,
    } = {},
    additionalRooms: {
      additionalBathrooms,
      bonusRoom,
      bonusRoomLivingSpaceImpacts,
      bonusRoomLocation,
      bonusRoomSqft,
      familyRoom,
      familyRoomLivingSpaceImpacts,
      familyRoomLocation,
      familyRoomSqft,
      flex,
      flexLivingSpaceImpacts,
      flexLocation,
      flexSize,
      flexSqft,
      gameRoom,
      gameRoomLivingSpaceImpacts,
      gameRoomLocation,
      gameRoomSqft,
      greatRoom,
      greatRoomLivingSpaceImpacts,
      greatRoomLocation,
      greatRoomSqft,
      loft,
      loftLivingSpaceImpacts,
      loftLocation,
      loftSqft,
      numberOfFixtures,
      study,
      studyLivingSpaceImpacts,
      studyLocation,
      studySqft,
      sunRoom,
      sunRoomLivingSpaceImpacts,
      sunRoomLocation,
      sunRoomSqft,
    } = {},
    garages: {
      garageLoad,
      garageSize,
      garageSize2,
      garageType,
      golfCartGarage,
      golfCartGarageAttachedDetached,
      golfCartGarageSize,
      numberOfCarGarages,
      numberOfCarGarages2,
      optionalGarage,
      optionalGarageNumberOfCarGarages,
      optionalGarageSize,
      optionalGarageType,
      rvGarage,
      rvGarageAttachedDetached,
      rvGarageSize,
    } = {},
    planArea: {
      basementSqft,
      depthOfThePlanInFeet,
      depthOfThePlanInInch,
      homeSqft,
      sqftOfHomeConditionedSpace,
      sqftOfHomeUnderRoof,
      widthOfThePlanInFeet,
      widthOfThePlanInInch,
    } = {},
    planOverview: {
      architecturalFirm,
      buildingSqft,
      changeNumber,
      communityName,
      customerSegment,
      engineerName,
      engineerOfRecord,
      firmName,
      marketingName,
      planOwnedByLennar,
      planStatus,
      planType,
      series,
      valueEngineered,
      yearOfCreation,
    } = {},
    planPrice: {
      baseHomePrice,
      baseHousePre,
      effectiveDate,
      targetProfitPerc,
    } = {},
    planStructure: {
      basement,
      basementType,
      bathrooms,
      bedrooms,
      formalDiningRoom,
      formalLivingRoom,
      foundationType,
      livingAreas,
      masterBedroomLocation,
      numberOfDiningAreas,
      stories,
    } = {},
    specifications: { marketingMaterials, modsy, virtualTours } = {},
    structureSpecification: {
      architecturalShake,
      architecturalShingle,
      boardAndBatten,
      entry,
      entrySqft,
      exteriorCladding,
      exteriorWallConstruction,
      garageAlignment,
      interiorWallConstruction,
      materialRoofType,
      roofShape,
      sidingLap,
      sidingPanel,
    } = {},
    supportingDocuments = [],
  } = planData
  const {
    communityPlanId,
    parentInfo,
    plan,
    planArea,
    planElevation,
    planId,
    planOverview,
    region,
  } = planData

  const planMasterData: PlanMasterData = {
    architecturalFirm: architecturalFirm ?? '',
    baseHousePre: baseHousePre?.toString() ?? '',
    baseHousePrice: baseHomePrice?.toString() ?? '',
    basement: basement ?? '',
    basementSqft: Number(basementSqft),
    basementType: basementType ?? '',
    bathrooms,
    bedrooms: bedrooms?.toString() ?? '',
    buildingSqft: buildingSqft ?? '',
    changeNumber: changeNumber ?? '',
    CommunityName: communityName ?? '',
    customerSegment: customerSegment ?? '',
    effectiveDate: effectiveDate ?? '',
    eighthFloorSqft: planArea?.eighthFloorSqft ?? 0,
    engineerName: engineerName ?? '',
    engineerOfRecord: engineerOfRecord ?? '',
    fifthFloorSqft: planArea?.fifthFloorSqft ?? 0,
    firmName: firmName ?? '',
    firstFloorSqft: planArea?.firstFloorSqft ?? 0,
    floorCeilingHeightList: planArea
      ? getDimensionValueFloors(planArea, stories)
      : [],
    formalLivingRoom: returnUndefinedIfUseDefaults(
      formalLivingRoom,
      useDefaults
    ),
    foundationType: foundationType ?? '',
    fourthFloorSqft: planArea?.fourthFloorSqft ?? 0,
    homeSqft: homeSqft ?? 0,
    livingAreas: livingAreas ?? 0,
    marketingName: marketingName ?? '',
    masterBedroomLocation: masterBedroomLocation
      ? Number(masterBedroomLocation)
      : useDefaults
        ? 1
        : undefined,
    ninthFloorSqft: planArea?.ninthFloorSqft ?? 0,
    PlanElevation: planElevation ?? '',
    planOwnedByLennar: planOwnedByLennar ?? '',
    planStatus: planStatus ?? '',
    planType: planType ?? '',
    profitPercentage: targetProfitPerc ?? '',
    secondFloorSqft: planArea?.secondFloorSqft ?? 0,
    series: series ?? '',
    seventhFloorSqft: planArea?.seventhFloorSqft ?? 0,
    sixthFloorSqft: planArea?.sixthFloorSqft ?? 0,
    sqftOfHomeConditionedSpace: sqftOfHomeConditionedSpace ?? 0,
    sqftOfHomeUnderRoof,
    stories: stories?.toString() ?? '',
    thirdFloorSqft: planArea?.thirdFloorSqft ?? 0,
    valueEngineered: valueEngineered ?? '',
    yearOfCreation: yearOfCreation ?? undefined,
  }

  const additionalPlanAttributes = {
    architecturalShake:
      typeof architecturalShake === 'string'
        ? [architecturalShake]
        : architecturalShake ?? [],
    architecturalShingle:
      typeof architecturalShingle === 'string'
        ? [architecturalShingle]
        : architecturalShingle ?? [],
    boardAndBatten:
      typeof boardAndBatten === 'string'
        ? [boardAndBatten]
        : boardAndBatten ?? [],
    depthOfThePlanInFeet: depthOfThePlanInFeet ?? '',
    depthOfThePlanInInch: depthOfThePlanInInch ?? '',
    entry: entry ?? '',
    entrySqft: entrySqft ?? null,
    exteriorCladding:
      typeof exteriorCladding === 'string'
        ? [exteriorCladding]
        : exteriorCladding ?? [],
    exteriorWallConstruction:
      typeof exteriorWallConstruction === 'string'
        ? [exteriorWallConstruction]
        : exteriorWallConstruction ?? [],
    formalDiningRoom: returnUndefinedIfUseDefaults(
      formalDiningRoom,
      useDefaults
    ),
    garageAlignment: garageAlignment ?? '',
    garageLoad:
      typeof garageLoad === 'string' ? [garageLoad] : garageLoad ?? [],
    garageSize: garageSize ?? '',
    garageSize2: garageSize2 ?? '',
    garageType: garageType ?? '',
    golfCartGarage: mapBooleanToYN(golfCartGarage, useDefaults),
    golfCartGarageAttachedDetached: golfCartGarageAttachedDetached ?? '',
    golfCartGarageSize: golfCartGarageSize ?? '',
    interiorWallConstruction:
      typeof interiorWallConstruction === 'string'
        ? [interiorWallConstruction]
        : interiorWallConstruction ?? [],
    materialRoofType:
      typeof materialRoofType === 'string'
        ? [materialRoofType]
        : materialRoofType ?? [],
    numberOfCarGarages: numberOfCarGarages ?? '',
    numberOfCarGarages2: numberOfCarGarages2 ?? '',
    numberOfDiningAreas,
    optionalGarage: mapBooleanToYN(optionalGarage, useDefaults),
    optionalGarageNumberOfCarGarages: optionalGarageNumberOfCarGarages ?? '',
    optionalGarageSize: optionalGarageSize ?? '',
    optionalGarageType: optionalGarageType ?? '',
    roofShape: typeof roofShape === 'string' ? [roofShape] : roofShape ?? [],
    rvGarage: mapBooleanToYN(rvGarage, useDefaults),
    rvGarageAttachedDetached: rvGarageAttachedDetached ?? '',
    rvGarageSize: rvGarageSize ?? '',
    sidingLap: typeof sidingLap === 'string' ? [sidingLap] : sidingLap ?? [],
    sidingPanel:
      typeof sidingPanel === 'string' ? [sidingPanel] : sidingPanel ?? [],
    widthOfThePlanInFeet: widthOfThePlanInFeet ?? '',
    widthOfThePlanInInch: widthOfThePlanInInch ?? '',
  }

  const additionalOptionsData = {
    additionalBathrooms: returnUndefinedIfUseDefaults(
      additionalBathrooms,
      useDefaults
    ),
    bonusRoom: returnUndefinedIfUseDefaults(bonusRoom, useDefaults),
    bonusRoomLivingSpaceImpacts: bonusRoomLivingSpaceImpacts ?? '',
    bonusRoomLocation: bonusRoomLocation ?? 0,
    bonusRoomSqft,
    deck: returnUndefinedIfUseDefaults(deck, useDefaults),
    deckSize: deckSize ?? '',

    familyRoom: returnUndefinedIfUseDefaults(familyRoom, useDefaults),
    familyRoomLivingSpaceImpacts: familyRoomLivingSpaceImpacts ?? '',
    familyRoomLocation: familyRoomLocation ?? 0,
    familyRoomSqft,
    flex: returnUndefinedIfUseDefaults(flex, useDefaults),
    flexLivingSpaceImpacts: flexLivingSpaceImpacts ?? '',
    flexLocation: flexLocation ?? 0,
    flexSize: flexSize ?? '',
    flexSqft,
    gameRoom: returnUndefinedIfUseDefaults(gameRoom, useDefaults),
    gameRoomLivingSpaceImpacts: gameRoomLivingSpaceImpacts ?? '',
    gameRoomLocation: gameRoomLocation ?? 0,
    gameRoomSqft,
    greatRoom: returnUndefinedIfUseDefaults(greatRoom, useDefaults),
    greatRoomLivingSpaceImpacts: greatRoomLivingSpaceImpacts ?? '',
    greatRoomLocation: greatRoomLocation ?? 0,
    greatRoomSqft,
    impactWindows: returnUndefinedIfUseDefaults(impactWindows, useDefaults),
    indoorFireplace: returnUndefinedIfUseDefaults(indoorFireplace, useDefaults),
    interiorSprinklers: returnUndefinedIfUseDefaults(
      interiorSprinklers,
      useDefaults
    ),
    lanai: returnUndefinedIfUseDefaults(lanaiEnclosures, useDefaults),

    lanaiEnclosureType: lanaiEnclosureType ?? '',
    lanaiSqft: lanaiSqft ?? '',
    loft: returnUndefinedIfUseDefaults(loft, useDefaults),
    loftLivingSpaceImpacts: loftLivingSpaceImpacts ?? '',
    loftLocation: loftLocation ?? 0,
    loftSqft,
    numberOfFixtures: numberOfFixtures ?? 0,
    numberOfIndoorFireplaces: numberOfIndoorFireplaces ?? 0,
    numberOfOutdoorFireplaces: numberOfOutdoorFireplaces ?? 0,
    outdoorFireplace: returnUndefinedIfUseDefaults(
      outdoorFireplace,
      useDefaults
    ),
    outdoorKitchen: returnUndefinedIfUseDefaults(outdoorKitchen, useDefaults),
    patio: returnUndefinedIfUseDefaults(patio, useDefaults),
    patioCovering: patioCovering ?? '',
    patioSize: patioSize ?? '',
    stormShutters: returnUndefinedIfUseDefaults(stormShutters, useDefaults),
    study: returnUndefinedIfUseDefaults(study, useDefaults),
    studyLivingSpaceImpacts: studyLivingSpaceImpacts ?? '',
    studyLocation: studyLocation ?? 0,
    studySqft,
    sunRoom: returnUndefinedIfUseDefaults(sunRoom, useDefaults),
    sunRoomLivingSpaceImpacts: sunRoomLivingSpaceImpacts ?? '',
    sunRoomLocation: sunRoomLocation ?? 0,
    sunRoomSqft,
    vaultedCeiling: returnUndefinedIfUseDefaults(vaultedCeiling, useDefaults),
    vaultedCeilingLivingSpaceImpacts: vaultedCeilingLivingSpaceImpacts ?? '',
    vaultedCeilingLocation: vaultedCeilingLocation ?? [],
  }

  const specifications = {
    marketingMaterials: marketingMaterials ?? [],
    modsy: modsy ?? [],
    virtualTours: virtualTours ?? [],
  }

  const supplementalDocuments = !supportingDocuments ? [] : supportingDocuments

  const path = window.location.pathname
  const planIdInUrl = path.split('/')[2]

  const stillCopyingSamePlan =
    parentInfo?.parentOrChildParentId?.toLowerCase() ===
    planIdInUrl?.toLowerCase()

  let newParentInfo = parentInfo
  if (location.pathname.includes('copy-plan') && !stillCopyingSamePlan) {
    newParentInfo = {
      parentOrChildParentId: planData.planId,
      parentPlanName: planData.planName,
      typeOfParent: parentInfo?.typeOfParent === 'parent' ? 'child' : 'parent',
      versionId: planData.versionId,
    }
  } else if (location.pathname.includes('add-plan-elevation')) {
    if (parentInfo?.parentOrChildParentId) {
      // Main plan is CHILD - new elevation becomes sibling with same parent
      newParentInfo = {
        parentOrChildParentId: parentInfo.parentOrChildParentId,
        parentPlanName: parentInfo.parentPlanName,
        typeOfParent: parentInfo.typeOfParent,
        versionId: parentInfo.versionId,
      }
    } else {
      // Main plan is PARENT - new elevation has no parent relationship
      newParentInfo = undefined
    }
  }

  // A consolidated Approved Regional Plan reaches the FE with its elevations and
  // its division codes already COMMA-JOINED: GET /plan declares both scalar, so
  // app-plan's normalizeMultiValuedFields joins the arrays on the way out. Mapping
  // the joined string straight into the form gave the intake form ONE elevation
  // literally named 'A4, C4, D4' and a divisionCode of 'JAX, MFL'. Splitting first
  // is a no-op for every single-valued plan, which yields exactly one entry.
  const elevationValues = splitMultiValued(planElevation)
  const planElevations: IPlanElevation[] = (
    elevationValues.length > 0 ? elevationValues : [planElevation]
  ).map((elevationValue) => {
    const elevationOption = cloneDeep(planElevationObj)
    elevationOption.value = elevationValue
    return elevationOption
  })

  const divisionCodes = splitMultiValued(planData.division)
  const primaryDivisionCode =
    divisionCodes.length > 0 ? divisionCodes[0] : planData.division

  const isApprovedRegionalPlan =
    planOverview?.planCategory === planCategories.APPROVED_REGIONAL_PLAN

  const planInfoData = {
    _etag: planData?._etag,
    communityData: getCommunityData(planOverview),
    communityNumber: getCommunityData(planOverview),
    communityPlanId,
    derivedFromApprovedRegionalPlan:
      (planData as { derivedFromApprovedRegionalPlan?: boolean })
        ?.derivedFromApprovedRegionalPlan === true,
    divisionCode: primaryDivisionCode,
    hidden: planData?.hidden,
    isCeilingHeightsRequired: planData?.isCeilingHeightsRequired,
    isLegacyPlan: planData?.isLegacyPlan,
    plan,
    planCategory: planOverview?.planCategory,
    planElevation: planElevations,
    planId: [planId],
    planOverview,
    planType,
    region,
    submitted: planData?.submitted,
  }

  return {
    additionalOptionsData,
    additionalPlanAttributes,
    parentInfo: newParentInfo,
    // An Approved Regional Plan spans several divisions, so keep the full
    // comma-joined list (e.g. 'JAX, MFL') for the intake header, which lists
    // every division. divisionCode stays the single primary code the submit
    // paths depend on; non-ARP plans keep their existing division untouched.
    planInfoData: isApprovedRegionalPlan
      ? { ...planInfoData, division: planData.division }
      : planInfoData,
    planMasterData,
    specifications,
    supplementalDocuments,
  }
}

export const getCommunityData = (
  planOverview: PlanOverview | ShellOverview | UnitOverview
): Array<ICommunityNumber> | undefined => {
  const category =
    'shellCategory' in planOverview
      ? planOverview.shellCategory
      : 'unitCategory' in planOverview
        ? planOverview.unitCategory
        : planOverview.planCategory

  if (category === 'community') {
    return [
      {
        cNameShowErrorMsg: false,
        cNameVal: {
          communityName: planOverview?.communityName,
        },
        cNumberIsEmptyError: false,
        cNumberVal: planOverview?.communityId || '',
        communityName: planOverview?.communityName || '',
        communityNumberLabel: planOverview?.communityId || '',
        divisionValidationError: false,
        error: false,
        loading: false,
        submitError: false,
        submitSuccess: false,
      },
    ]
  } else return undefined
}

export const getDimensionValueFloors = (
  planArea: PlanArea,
  stories?: string | number
) => {
  const dimensionValueOfFloors = []
  if (planArea && stories) {
    for (let i = 1; i <= Number(stories); i++) {
      const floorCeilingHeightInFeetKey = `${formatFloorKey(
        i - 1
      )}FloorCeilingHeightInFeet` as keyof PlanArea
      const floorCeilingHeightInFeet = planArea[floorCeilingHeightInFeetKey]
      const floorCeilingHeightInInchKey = `${formatFloorKey(
        i - 1
      )}FloorCeilingHeightInInch` as keyof PlanArea
      const floorCeilingHeightInInch = planArea[floorCeilingHeightInInchKey]
      dimensionValueOfFloors.push({
        label: `${i}<sup>${toOrdinal(i)}</sup>&nbsp; ${
          COLUMN_LABELS.FLOOR_CEILING_HEIGHT
        }`,
        valueInFeet: floorCeilingHeightInFeet,
        valueInInch: floorCeilingHeightInInch,
      })
    }
    return dimensionValueOfFloors
  }
}

export { toPlanToView }
